import {
  blockCompanyBody,
  COMPANY_BULK_MAX_IDS,
  type CompanyBulkAction,
  type CompanyStatus,
  unblockCompanyBody,
} from '@planuze/admin-contracts';
import {
  Button,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { CompaniesFilters, type StatusFilter } from '../components/companies/companies-filters.js';
import { CompaniesTable } from '../components/companies/companies-table.js';
import { CompanyBulkBar } from '../components/companies/company-bulk-bar.js';
import { CompanyReasonDialog } from '../components/companies/company-reason-dialog.js';
import { CreateCompanyModal } from '../components/companies/create-company-modal.js';
import { useCompaniesListContent } from '../content/sections/companies-list.js';
import { useBulkCompanyAction, useCompaniesList } from '../hooks/use-companies.js';
import { useDebounce } from '../hooks/use-debounce.js';
import { useLimitedBulkSelection } from '../hooks/use-limited-bulk-selection.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

export function CompaniesListPage() {
  const navigate = useNavigate();
  const session = useStaffSession();
  const copy = useCompaniesListContent();
  /* Provisionar org é admin-created (ADR-0421) — só super_admin. */
  const canCreate = session?.role === 'super_admin';
  const canManage = session?.role === 'super_admin' || session?.role === 'support';
  const bulk = useBulkCompanyAction();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [pendingBulk, setPendingBulk] = useState<{
    readonly ids: readonly string[];
    readonly action: CompanyBulkAction;
  } | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCompaniesList({
      search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
      status: status === 'all' ? undefined : (status as CompanyStatus),
    });
  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const visibleIds = useMemo(() => items.map((item) => item.id), [items]);
  const selection = useLimitedBulkSelection(visibleIds, COMPANY_BULK_MAX_IDS);

  const prepareBulk = (action: CompanyBulkAction): void => {
    const ids = [...selection.selectedIds];
    if (ids.length > 0) setPendingBulk({ ids, action });
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.title}</PageTitle>
          <PageDescription>{copy.description}</PageDescription>
        </PageTitleBlock>
        {canCreate && (
          <PageActions>
            <Button onClick={() => setCreateOpen(true)}>{copy.createOrg}</Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        {/* Busca + status são filtros server-side já suportados por listCompaniesQuery.
            follow-up: filtros por createdAt (intervalo) e memberCount (faixa) são server-side e
            precisam de campos novos no contrato listCompaniesQuery + no admin-service — não
            dá para filtrar client-side porque a lista é paginada (só a página atual chega). */}
        <CompaniesFilters
          value={{ search, status }}
          onChange={(value) => {
            setSearch(value.search);
            setStatus(value.status);
          }}
        />
        {canManage && (
          <CompanyBulkBar
            selectedCount={selection.selectedIds.size}
            busy={bulk.isPending}
            onBlock={() => prepareBulk('block')}
            onUnblock={() => prepareBulk('unblock')}
            onClear={selection.clear}
          />
        )}
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadFailed}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canManage ? 5 : 4} />
        ) : (
          <CompaniesTable
            items={items}
            canManage={canManage}
            selectedIds={selection.selectedIds}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
            hasFilters={debouncedSearch.length > 0 || status !== 'all'}
            onClearFilters={() => {
              setSearch('');
              setStatus('all');
            }}
            onRowClick={(id) => {
              navigate({ to: '/companies/$id', params: { id } }).catch(() => undefined);
            }}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMore}
            </Button>
          </div>
        )}
      </PageContent>
      <CreateCompanyModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <CompanyReasonDialog
        open={pendingBulk !== null}
        onClose={() => setPendingBulk(null)}
        title={pendingBulk?.action === 'block' ? copy.bulk.blockTitle : copy.bulk.unblockTitle}
        description={copy.bulk.description(
          pendingBulk?.action === 'block' ? copy.bulk.block : copy.bulk.unblock,
          pendingBulk?.ids.length ?? 0,
        )}
        confirmLabel={pendingBulk?.action === 'block' ? copy.bulk.block : copy.bulk.unblock}
        destructive={pendingBulk?.action === 'block'}
        pending={bulk.isPending}
        validate={(reason) =>
          (pendingBulk?.action === 'block' ? blockCompanyBody : unblockCompanyBody).safeParse({
            reason,
          }).success
        }
        onConfirm={(reason) => {
          const target = pendingBulk;
          if (target === null) return;
          bulk.mutate(
            { ids: [...target.ids], action: target.action, reason },
            {
              onSuccess: (response) => {
                const requiresReview = response.results.filter(
                  (result) => !result.ok || result.outcome.status === 'partial_success',
                );
                if (requiresReview.length === 0)
                  toast.success(copy.bulk.success(response.results.length));
                else
                  toast.warning(
                    copy.bulk.partial(
                      response.results.length - requiresReview.length,
                      requiresReview.length,
                    ),
                  );
                selection.replace(requiresReview.map((result) => result.id));
                setPendingBulk(null);
              },
              onError: () => toast.error(copy.bulk.error),
            },
          );
        }}
      />
    </Page>
  );
}
